import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { compareNl } from '../../../utils/sort';
import { logProjectHistory } from '../../../utils/projectHistory';
import type { ProjectAddress } from '../../planning/types';
import type { SettingsProjectRow } from '../types';

const byYearThenName = (a: SettingsProjectRow, b: SettingsProjectRow) =>
  b.year - a.year || compareNl(a.name, b.name);

interface UseManageProjectsReturn {
  projects: SettingsProjectRow[];
  archivedProjects: SettingsProjectRow[];
  archivedLoaded: boolean;
  loadArchived: () => Promise<void>;
  addProject: (name: string, year: number) => Promise<'ok' | 'limit_exceeded' | 'error'>;
  updateProject: (id: string, name: string, year: number) => Promise<boolean>;
  archiveProject: (id: string) => Promise<boolean>;
  unarchiveProject: (id: string) => Promise<'ok' | 'limit_exceeded' | 'error'>;
  deleteProject: (id: string) => Promise<'ok' | 'has_machines' | 'error'>;
  addAddress: (projectId: string, name: string, street: string, houseNumber: string, postalCode: string, city: string) => Promise<boolean>;
  updateAddress: (id: string, name: string, street: string, houseNumber: string, postalCode: string, city: string) => Promise<boolean>;
  deleteAddress: (id: string) => Promise<boolean>;
  setDefaultAddress: (addressId: string, projectId: string) => Promise<boolean>;
}

type RawProject = Omit<SettingsProjectRow, 'has_machines'> & { has_machines?: boolean };

async function buildRows(data: RawProject[]): Promise<SettingsProjectRow[]> {
  const ids = data.map((p) => p.id);
  const { data: machines } = ids.length
    ? await supabase.from('machines').select('project_id').in('project_id', ids)
    : { data: [] };
  const withMachines = new Set((machines ?? []).map((m) => m.project_id));
  return data.map((p) => ({
    ...p,
    has_machines: withMachines.has(p.id),
    project_addresses: p.project_addresses ?? [],
  }));
}

export function useManageProjects(orgId: string | null): UseManageProjectsReturn {
  const [projects, setProjects] = useState<SettingsProjectRow[]>([]);
  const [archivedProjects, setArchivedProjects] = useState<SettingsProjectRow[]>([]);
  const [archivedLoaded, setArchivedLoaded] = useState(false);

  const loadActive = async () => {
    if (!orgId) { setProjects([]); return; }
    try {
      const { data } = await supabase
        .from('projects')
        .select('*, project_addresses(*)')
        .eq('organization_id', orgId)
        .eq('archived', false)
        .order('year', { ascending: false })
        .order('name');
      setProjects(await buildRows(data ?? []));
    } catch {
      setProjects([]);
    }
  };

  const refreshArchived = async (): Promise<boolean> => {
    if (!orgId) return false;
    try {
      const { data } = await supabase
        .from('projects')
        .select('*, project_addresses(*)')
        .eq('organization_id', orgId)
        .eq('archived', true)
        .order('year', { ascending: false })
        .order('name');
      setArchivedProjects(await buildRows(data ?? []));
      return true;
    } catch {
      setArchivedProjects([]);
      return false;
    }
  };

  const loadArchived = async () => {
    if (!orgId || archivedLoaded) return;
    setArchivedLoaded(true);
    const ok = await refreshArchived();
    if (!ok) setArchivedLoaded(false);
  };

  useEffect(() => {
    setArchivedLoaded(false);
    setArchivedProjects([]);
    loadActive();
  }, [orgId]);

  const isUnderActiveProjectLimit = async (): Promise<boolean> => {
    const { data: org } = await supabase
      .from('organizations')
      .select('max_active_projects')
      .eq('id', orgId)
      .single();
    if (org?.max_active_projects == null) return true;
    const { count } = await supabase
      .from('projects')
      .select('*', { count: 'exact', head: true })
      .eq('organization_id', orgId)
      .eq('archived', false);
    return (count ?? 0) < org.max_active_projects;
  };

  const addProject = async (name: string, year: number): Promise<'ok' | 'limit_exceeded' | 'error'> => {
    if (!orgId) return 'error';
    try {
      if (!(await isUnderActiveProjectLimit())) return 'limit_exceeded';
      const { data, error } = await supabase
        .from('projects')
        .insert({ name, year, organization_id: orgId, archived: false })
        .select('id')
        .single();
      if (error) throw error;
      await loadActive();
      window.dispatchEvent(new CustomEvent('planly:projects-changed'));
      if (data) {
        void logProjectHistory({
          projectId: data.id,
          organizationId: orgId,
          action: 'create',
          entityType: 'project',
          entityName: name,
          description: `Project '${name}' aangemaakt`,
        });
      }
      return 'ok';
    } catch {
      return 'error';
    }
  };

  const updateProject = async (id: string, name: string, year: number): Promise<boolean> => {
    try {
      const previous = projects.find((p) => p.id === id) ?? archivedProjects.find((p) => p.id === id);
      const { error } = await supabase.from('projects').update({ name, year }).eq('id', id);
      if (error) throw error;
      const update = (list: SettingsProjectRow[]) =>
        list.map((p) => (p.id === id ? { ...p, name, year } : p)).sort(byYearThenName);
      setProjects(update);
      setArchivedProjects(update);
      window.dispatchEvent(new CustomEvent('planly:projects-changed'));
      if (previous) {
        const changes: string[] = [];
        if (previous.name !== name) changes.push(`Project hernoemd van '${previous.name}' naar '${name}'`);
        if (previous.year !== year) changes.push(`Jaar gewijzigd van ${previous.year} naar ${year}`);
        if (changes.length > 0) {
          void logProjectHistory({
            projectId: id,
            organizationId: orgId,
            action: 'update',
            entityType: 'project',
            entityName: name,
            description: changes.join('; '),
          });
        }
      }
      return true;
    } catch {
      return false;
    }
  };

  const archiveProject = async (id: string): Promise<boolean> => {
    try {
      const project = projects.find((p) => p.id === id);
      const { error } = await supabase.from('projects').update({ archived: true }).eq('id', id);
      if (error) throw error;
      setProjects((prev) => prev.filter((p) => p.id !== id));
      if (archivedLoaded) await refreshArchived();
      window.dispatchEvent(new CustomEvent('planly:projects-changed'));
      if (project) {
        void logProjectHistory({
          projectId: id,
          organizationId: orgId,
          action: 'archive',
          entityType: 'project',
          entityName: project.name,
          description: `Project gearchiveerd`,
        });
      }
      return true;
    } catch { return false; }
  };

  const unarchiveProject = async (id: string): Promise<'ok' | 'limit_exceeded' | 'error'> => {
    try {
      if (!(await isUnderActiveProjectLimit())) return 'limit_exceeded';
      const project = archivedProjects.find((p) => p.id === id);
      const { error } = await supabase.from('projects').update({ archived: false }).eq('id', id);
      if (error) throw error;
      setArchivedProjects((prev) => prev.filter((p) => p.id !== id));
      await loadActive();
      window.dispatchEvent(new CustomEvent('planly:projects-changed'));
      if (project) {
        void logProjectHistory({
          projectId: id,
          organizationId: orgId,
          action: 'unarchive',
          entityType: 'project',
          entityName: project.name,
          description: `Project gedearchiveerd`,
        });
      }
      return 'ok';
    } catch { return 'error'; }
  };

  const deleteProject = async (id: string): Promise<'ok' | 'has_machines' | 'error'> => {
    try {
      const { count } = await supabase
        .from('machines')
        .select('*', { count: 'exact', head: true })
        .eq('project_id', id);
      if ((count ?? 0) > 0) return 'has_machines';
      const { error } = await supabase.from('projects').delete().eq('id', id);
      if (error) throw error;
      setProjects((prev) => prev.filter((p) => p.id !== id));
      setArchivedProjects((prev) => prev.filter((p) => p.id !== id));
      window.dispatchEvent(new CustomEvent('planly:projects-changed'));
      return 'ok';
    } catch {
      return 'error';
    }
  };

  const findAddressContext = (id: string): { projectId: string; addressName: string } | null => {
    for (const p of [...projects, ...archivedProjects]) {
      const address = p.project_addresses.find((a) => a.id === id);
      if (address) return { projectId: p.id, addressName: address.name };
    }
    return null;
  };

  const addAddress = async (
    projectId: string,
    name: string,
    street: string,
    houseNumber: string,
    postalCode: string,
    city: string,
  ): Promise<boolean> => {
    try {
      const projectInActive = projects.find((p) => p.id === projectId);
      const projectInArchived = archivedProjects.find((p) => p.id === projectId);
      const existingCount = (projectInActive ?? projectInArchived)?.project_addresses.length ?? 0;
      const isFirst = existingCount === 0;

      const { data, error } = await supabase
        .from('project_addresses')
        .insert({ project_id: projectId, name, street, house_number: houseNumber, postal_code: postalCode, city, is_default: isFirst })
        .select()
        .single();
      if (error) throw error;

      const newAddress = data as ProjectAddress;
      const updateList = (list: SettingsProjectRow[]) =>
        list.map((p) =>
          p.id === projectId
            ? { ...p, project_addresses: [...p.project_addresses, newAddress] }
            : p
        );
      setProjects(updateList);
      setArchivedProjects(updateList);
      void logProjectHistory({
        projectId,
        organizationId: orgId,
        action: 'create',
        entityType: 'address',
        entityName: name,
        description: `Adres '${name}' toegevoegd`,
      });
      return true;
    } catch {
      return false;
    }
  };

  const updateAddress = async (
    id: string,
    name: string,
    street: string,
    houseNumber: string,
    postalCode: string,
    city: string,
  ): Promise<boolean> => {
    try {
      const context = findAddressContext(id);
      const { error } = await supabase
        .from('project_addresses')
        .update({ name, street, house_number: houseNumber, postal_code: postalCode, city })
        .eq('id', id);
      if (error) throw error;

      const updateList = (list: SettingsProjectRow[]) =>
        list.map((p) => ({
          ...p,
          project_addresses: p.project_addresses.map((a) =>
            a.id === id ? { ...a, name, street, house_number: houseNumber, postal_code: postalCode, city } : a
          ),
        }));
      setProjects(updateList);
      setArchivedProjects(updateList);
      if (context) {
        void logProjectHistory({
          projectId: context.projectId,
          organizationId: orgId,
          action: 'update',
          entityType: 'address',
          entityName: name,
          description: `Adres '${name}' bijgewerkt`,
        });
      }
      return true;
    } catch {
      return false;
    }
  };

  const deleteAddress = async (id: string): Promise<boolean> => {
    try {
      const context = findAddressContext(id);
      const { error } = await supabase.from('project_addresses').delete().eq('id', id);
      if (error) throw error;

      const updateList = (list: SettingsProjectRow[]) =>
        list.map((p) => ({
          ...p,
          project_addresses: p.project_addresses.filter((a) => a.id !== id),
        }));
      setProjects(updateList);
      setArchivedProjects(updateList);
      if (context) {
        void logProjectHistory({
          projectId: context.projectId,
          organizationId: orgId,
          action: 'delete',
          entityType: 'address',
          entityName: context.addressName,
          description: `Adres '${context.addressName}' verwijderd`,
        });
      }
      return true;
    } catch {
      return false;
    }
  };

  const setDefaultAddress = async (addressId: string, projectId: string): Promise<boolean> => {
    try {
      const context = findAddressContext(addressId);
      const { error: clearError } = await supabase
        .from('project_addresses')
        .update({ is_default: false })
        .eq('project_id', projectId);
      if (clearError) throw clearError;
      const { error } = await supabase
        .from('project_addresses')
        .update({ is_default: true })
        .eq('id', addressId);
      if (error) throw error;

      const updateList = (list: SettingsProjectRow[]) =>
        list.map((p) =>
          p.id === projectId
            ? {
                ...p,
                project_addresses: p.project_addresses.map((a) => ({
                  ...a,
                  is_default: a.id === addressId,
                })),
              }
            : p
        );
      setProjects(updateList);
      setArchivedProjects(updateList);
      if (context) {
        void logProjectHistory({
          projectId,
          organizationId: orgId,
          action: 'update',
          entityType: 'address',
          entityName: context.addressName,
          description: `Adres '${context.addressName}' als standaard ingesteld`,
        });
      }
      return true;
    } catch { return false; }
  };

  return {
    projects,
    archivedProjects,
    archivedLoaded,
    loadArchived,
    addProject,
    updateProject,
    archiveProject,
    unarchiveProject,
    deleteProject,
    addAddress,
    updateAddress,
    deleteAddress,
    setDefaultAddress,
  };
}
